import {
  Canvas,
  LengthFormat,
  resolveLengthFormat,
  UnitSystem,
} from '@ritabygglov/core';
import { siteConfig } from '../../../config/siteConfig';

// A returning user's own last choice is a stronger signal than any browser
// heuristic — if they already have a project, match its unit system rather
// than guessing.
const getMostRecentCanvas = (canvases: Canvas[]): Canvas | undefined =>
  [...canvases].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0];

// ritabygglov.se is a Sweden-only service with an established metric user
// base, so it must never guess imperial from the browser — a Swedish user
// with an en-US locale (expat, US-region OS install, etc.) would otherwise
// get an unwanted imperial default. drawblueprint.com has no such legacy
// users, so a locale guess there is low-stakes. Only used for a user's
// first-ever project, when there's no prior canvas to learn from.
const getDefaultUnitSystemForNewUser = (): UnitSystem =>
  siteConfig.site === 'drawblueprint' && navigator.language === 'en-US'
    ? 'imperial'
    : 'metric';

export const getDefaultUnitSystem = (canvases: Canvas[]): UnitSystem => {
  const mostRecentCanvas = getMostRecentCanvas(canvases);
  return mostRecentCanvas?.unitSystem ?? getDefaultUnitSystemForNewUser();
};

// Same reasoning as the unit system: carry over the last project's notation
// so someone who draws in millimetres doesn't re-pick it every time. Resolved
// against the unit system currently selected in the form, which the user may
// have changed away from the one that format belongs to.
export const getDefaultLengthFormat = (
  canvases: Canvas[],
  unitSystem: UnitSystem
): LengthFormat =>
  resolveLengthFormat(
    unitSystem,
    getMostRecentCanvas(canvases)?.lengthFormat ?? 'meters'
  );
